/**
 * Transactional graph edit operations — the "basic functions" the LLM calls
 * instead of emitting raw graph JSON. Ops are data so they batch into a
 * single tool call; the whole batch applies atomically or not at all.
 *
 * Non-mutating: the input graph is never touched (copy-on-write). After all
 * ops apply, the result is validated; NEW renderable errors (relative to the
 * input graph's baseline) fail the batch.
 */
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import type { UniversalNodeSchema } from '@/features/backtest/schemas/universal-node-schema'
import type { GraphNode, GraphEdge } from '@/features/backtest/schemas/backtest-types'
import { materializeNode } from './materialize'
import { validateGraph } from './graph-validator'
import { validateNodeConnection } from '@/features/backtest/utils/universal-node-validation'
import { isPassthroughType } from '@/features/backtest/utils/inline-passthroughs'
import { canConnect, planParamChange } from '@/features/backtest/utils/graph-commands'
import type { Edge, Node } from '@xyflow/react'
import { guardSpineDelete } from '@/features/backtest/utils/spine-nodes'
import { redirectRunDayParams } from '@/features/backtest/utils/run-day-redirect'
import type { DraftGraph, EngineError, EngineResult, MaterializedInput } from './types'

export type GraphOp =
  | { op: 'add_node'; id: string; type: string; params?: Record<string, unknown> }
  | { op: 'set_params'; id: string; params: Record<string, unknown> }
  | { op: 'connect'; from: string; to: string }
  | { op: 'disconnect'; to: string }
  | { op: 'remove_node'; id: string }
  | {
      op: 'insert_between'
      id: string
      type: string
      from: string
      to: string
      params?: Record<string, unknown>
    }
  | { op: 'replace_node'; id: string; type: string; params?: Record<string, unknown> }

export interface ApplyResult {
  graph: DraftGraph
  /** stepId → nodeId, extended with entries for nodes the batch added. */
  idMap: Map<string, string>
  warnings: EngineError[]
}

const OHLCV_FIELDS = new Set(['open', 'high', 'low', 'close', 'volume'])

export function applyGraphOps(
  graph: DraftGraph,
  ops: GraphOp[],
  idMap: Map<string, string>,
): EngineResult<ApplyResult> {
  const ctx = new OpContext(graph, idMap)

  for (const op of ops) {
    applyOp(ctx, op)
    if (ctx.errors.length > 0) return { ok: false, errors: ctx.errors }
  }

  ctx.placeNewNodes()

  // Post-batch validation: fail only on errors the batch INTRODUCED, so a
  // pre-existing issue elsewhere in a live graph can't block unrelated edits.
  const baseline = errorKeySet(validateGraph(graph.nodes, graph.edges).errors)
  const report = validateGraph(ctx.nodes, ctx.edges)
  const introduced = report.errors.filter((e) => !baseline.has(errorKey(e)))
  if (introduced.length > 0) return { ok: false, errors: introduced }

  return {
    ok: true,
    value: {
      graph: { nodes: ctx.nodes, edges: ctx.edges },
      idMap: ctx.idMap,
      warnings: [...ctx.warnings, ...report.warnings],
    },
  }
}

function errorKey(e: EngineError): string {
  return `${e.code}|${e.stepId ?? ''}|${e.handle ?? ''}`
}
function errorKeySet(errors: EngineError[]): Set<string> {
  return new Set(errors.map(errorKey))
}

// ── Op context (copy-on-write working state) ────────────────

class OpContext {
  nodes: GraphNode[]
  edges: GraphEdge[]
  idMap: Map<string, string>
  errors: EngineError[] = []
  warnings: EngineError[] = []
  /** node ids added this batch that still need a position. */
  private unplaced = new Set<string>()
  private cloned = new Set<string>()

  constructor(graph: DraftGraph, idMap: Map<string, string>) {
    this.nodes = [...graph.nodes]
    this.edges = [...graph.edges]
    this.idMap = new Map(idMap)
  }

  nodeIndex(nodeId: string): number {
    return this.nodes.findIndex((n) => n.id === nodeId)
  }

  /** Resolve a stepId (preferred) or raw node id to a node id. */
  resolveId(part: string): string | null {
    const viaMap = this.idMap.get(part)
    if (viaMap && this.nodeIndex(viaMap) !== -1) return viaMap
    if (this.nodeIndex(part) !== -1) return part
    return null
  }

  schemaOf(nodeId: string): UniversalNodeSchema | undefined {
    const node = this.nodes[this.nodeIndex(nodeId)]
    return node ? UniversalNodeRegistry.getSchemaResolved(node.type) : undefined
  }

  /** Copy-on-write node mutation. */
  mutateNode(nodeId: string, fn: (node: GraphNode) => void): void {
    const idx = this.nodeIndex(nodeId)
    if (idx === -1) return
    let node = this.nodes[idx]
    if (!this.cloned.has(nodeId)) {
      node = {
        ...node,
        position: { ...node.position },
        data: {
          ...node.data,
          parameters: clonePlain(node.data.parameters),
          inputs: clonePlain(node.data.inputs),
        },
      }
      this.cloned.add(nodeId)
    }
    fn(node)
    this.nodes[idx] = node
  }

  addNode(node: GraphNode, stepId: string, needsPlacement: boolean): void {
    this.nodes.push(node)
    this.cloned.add(node.id)
    this.idMap.set(stepId, node.id)
    if (needsPlacement) this.unplaced.add(node.id)
  }

  fail(error: EngineError): void {
    this.errors.push(error)
  }

  /** Place batch-added nodes: beside their first neighbor, else below the graph. */
  placeNewNodes(): void {
    for (const nodeId of this.unplaced) {
      const idx = this.nodeIndex(nodeId)
      if (idx === -1) continue
      const neighborEdge = this.edges.find((e) => e.source === nodeId || e.target === nodeId)
      let position: { x: number; y: number }
      if (neighborEdge) {
        const neighborId =
          neighborEdge.source === nodeId ? neighborEdge.target : neighborEdge.source
        const neighbor = this.nodes[this.nodeIndex(neighborId)]
        const dx = neighborEdge.source === nodeId ? -250 : 250
        position = { x: (neighbor?.position.x ?? 0) + dx, y: neighbor?.position.y ?? 0 }
      } else {
        const placed = this.nodes.filter((n) => n.id !== nodeId && !this.unplaced.has(n.id))
        const minX = Math.min(...placed.map((n) => n.position.x), 50)
        const maxY = Math.max(...placed.map((n) => n.position.y), 0)
        position = { x: minX, y: maxY + 200 }
      }
      this.mutateNode(nodeId, (n) => {
        n.position = position
      })
    }
    this.unplaced.clear()
  }
}

function clonePlain<T>(value: T): T {
  if (Array.isArray(value)) return value.map((v) => clonePlain(v)) as T
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) out[k] = clonePlain(v)
    return out as T
  }
  return value
}

// ── Op dispatch ─────────────────────────────────────────────

function applyOp(ctx: OpContext, op: GraphOp): void {
  switch (op.op) {
    case 'add_node':
      return void opAddNode(ctx, op)
    case 'set_params':
      return opSetParams(ctx, op)
    case 'connect':
      return void opConnect(ctx, op.from, op.to)
    case 'disconnect':
      return opDisconnect(ctx, op)
    case 'remove_node':
      return opRemoveNode(ctx, op.id)
    case 'insert_between':
      return opInsertBetween(ctx, op)
    case 'replace_node':
      return opReplaceNode(ctx, op)
  }
}

// ── add_node ────────────────────────────────────────────────

function freshNodeId(ctx: OpContext, stepId: string): string {
  if (ctx.nodeIndex(stepId) === -1) return stepId
  let k = 2
  while (ctx.nodeIndex(`${stepId}-${k}`) !== -1) k++
  return `${stepId}-${k}`
}

function opAddNode(
  ctx: OpContext,
  op: { id: string; type: string; params?: Record<string, unknown> },
  position?: { x: number; y: number },
): GraphNode | null {
  const schema = UniversalNodeRegistry.getSchemaResolved(op.type)
  if (!schema) {
    ctx.fail({ code: 'UNKNOWN_TYPE', stepId: op.id, message: `Unknown node type "${op.type}"` })
    return null
  }
  if (ctx.idMap.has(op.id)) {
    ctx.fail({
      code: 'DUPLICATE_STEP_ID',
      stepId: op.id,
      message: `Step id "${op.id}" already exists`,
    })
    return null
  }
  const nodeId = freshNodeId(ctx, op.id)
  const result = materializeNode(
    { id: nodeId, type: schema.type, params: op.params },
    {
      schemaById: new Map([[nodeId, schema]]),
      position: position ?? { x: 0, y: 0 },
    },
  )
  if (result.errors.length > 0) {
    ctx.errors.push(...result.errors.map((e) => ({ ...e, stepId: op.id })))
    return null
  }
  ctx.addNode(result.node, op.id, position === undefined)
  return result.node
}

// ── set_params ──────────────────────────────────────────────

function opSetParams(ctx: OpContext, op: { id: string; params: Record<string, unknown> }): void {
  const nodeId = ctx.resolveId(op.id)
  if (!nodeId) {
    ctx.fail({ code: 'UNKNOWN_STEP', stepId: op.id, message: `Unknown step "${op.id}"` })
    return
  }
  // The run's ticker and day live on the connected Stock Selection now; a
  // Price Data set (older AI habits) lands there, the rest stays on the feed.
  const split = redirectRunDayParams(ctx.nodes, ctx.edges, nodeId, op.params)
  if (split.universeId) {
    opSetParams(ctx, { id: split.universeId, params: split.moved })
    if (ctx.errors.length > 0 || Object.keys(split.kept).length === 0) return
    op = { ...op, params: split.kept }
  }
  const schema = ctx.schemaOf(nodeId)
  if (!schema) {
    ctx.fail({ code: 'UNKNOWN_TYPE', stepId: op.id, message: `No schema for "${op.id}"` })
    return
  }
  const known = new Set(schema.parameters.map((p) => p.name))
  for (const name of Object.keys(op.params)) {
    if (!known.has(name)) {
      ctx.fail({
        code: 'PARAM_UNKNOWN',
        stepId: op.id,
        message: `"${schema.type}" has no parameter "${name}"`,
        suggestion: `Known parameters: ${[...known].join(', ')}`,
      })
      return
    }
  }
  // Each setting, with what it does beyond the value (the editor's one rule,
  // utils/graph-commands planParamChange): wires a mode lets go of, Node or
  // Static for an input, a subnet's exposure. Writing the value alone left a
  // Trade Manager switched back to Market holding its limit price wire.
  for (const [name, value] of Object.entries(op.params)) {
    const plan = planParamChange(
      { nodes: ctx.nodes as unknown as Node[], edges: ctx.edges as unknown as Edge[] },
      nodeId,
      name,
      value,
    )
    const gone = new Set(plan.delta.remove)
    const released = ctx.edges.filter((e) => gone.has(e.id))
    ctx.edges = ctx.edges.filter((e) => !gone.has(e.id))
    ctx.mutateNode(nodeId, (node) => {
      node.data.parameters = setParams(node.data.parameters, { [name]: value })
      if (plan.inputs) node.data.inputs = plan.inputs as unknown as MaterializedInput[]
    })
    for (const wire of released) {
      if (wire.targetHandle) resetInputEntry(ctx, wire.target, wire.targetHandle)
    }
    for (const { subnetId, exposure } of plan.exposure) {
      ctx.mutateNode(subnetId, (node) => {
        node.data.exposure = exposure
      })
    }
  }
}

/** Update parameters preserving their stored shape (descriptor array or record). */
function setParams(raw: unknown, updates: Record<string, unknown>): unknown {
  if (Array.isArray(raw)) {
    const out = raw.map((p) =>
      p && typeof p === 'object' && 'name' in p && (p as { name: string }).name in updates
        ? { ...(p as object), value: updates[(p as { name: string }).name] }
        : p,
    )
    const present = new Set(
      raw
        .filter((p) => p && typeof p === 'object' && 'name' in p)
        .map((p) => (p as { name: string }).name),
    )
    for (const [name, value] of Object.entries(updates)) {
      if (!present.has(name)) out.push({ name, value })
    }
    return out
  }
  return { ...(raw as Record<string, unknown> | undefined), ...updates }
}

// ── connect / disconnect ────────────────────────────────────

interface ResolvedEndpoint {
  nodeId: string
  handle: string
  /** OHLCV field when the from-ref used field sugar. */
  field?: string
  outputType?: string
}

function resolveFrom(ctx: OpContext, from: string): ResolvedEndpoint | null {
  const dot = from.indexOf('.')
  const part = dot === -1 ? from : from.slice(0, dot)
  const nodeId = ctx.resolveId(part)
  if (!nodeId) {
    ctx.fail({ code: 'UNKNOWN_STEP', message: `Unknown source "${part}"` })
    return null
  }
  // A reroute or subnet pill has no schema: one output, `out`. It used to
  // fall through to "no schema" and return nothing, so the batch reported ok
  // with no wire (audit 2026-10-06).
  if (isPassthroughType(ctx.nodes[ctx.nodeIndex(nodeId)]?.type)) {
    const handle = dot === -1 ? 'out' : from.slice(dot + 1)
    if (handle !== 'out') {
      ctx.fail({
        code: 'UNKNOWN_HANDLE',
        stepId: part,
        handle,
        message: `"${part}" passes its input through; its output is "out"`,
      })
      return null
    }
    return { nodeId, handle: 'out' }
  }
  const schema = ctx.schemaOf(nodeId)
  if (!schema) {
    ctx.fail(unknownType(ctx, part, nodeId))
    return null
  }

  if (dot === -1) {
    if (schema.outputs.length === 1) {
      const o = schema.outputs[0]
      return { nodeId, handle: o.id, outputType: o.type }
    }
    ctx.fail({
      code: 'AMBIGUOUS_HANDLE',
      stepId: part,
      message: `"${part}" has ${schema.outputs.length} outputs — specify one`,
      suggestion: `Candidates: ${schema.outputs.map((o) => o.id).join(', ')}`,
    })
    return null
  }

  const handlePart = from.slice(dot + 1)
  const ohlcvOutput = schema.outputs.find((o) => o.type === 'ohlcv')
  if (ohlcvOutput && OHLCV_FIELDS.has(handlePart)) {
    return { nodeId, handle: ohlcvOutput.id, field: handlePart, outputType: 'ohlcv' }
  }
  const output = schema.outputs.find((o) => o.id === handlePart)
  if (!output) {
    ctx.fail({
      code: 'UNKNOWN_HANDLE',
      stepId: part,
      handle: handlePart,
      message: `"${part}" has no output "${handlePart}"`,
      suggestion: `Available: ${schema.outputs.map((o) => o.id).join(', ')}`,
    })
    return null
  }
  return { nodeId, handle: output.id, outputType: output.type }
}

/** The error for a node whose type has no schema (and is not a pass-through). */
function unknownType(ctx: OpContext, stepId: string, nodeId: string): EngineError {
  const type = ctx.nodes[ctx.nodeIndex(nodeId)]?.type ?? 'unknown'
  return {
    code: 'UNKNOWN_TYPE',
    stepId,
    message: `"${stepId}" is a "${type}" node, which can't be wired from here`,
  }
}

/**
 * Whether a resolved source fits a target input, by the editor's own check
 * (utils/graph-commands): a pass-through source is checked as what feeds it.
 */
function fitsInput(
  ctx: OpContext,
  source: ResolvedEndpoint,
  targetId: string,
  handle: string,
): boolean {
  return canConnect(
    { nodes: ctx.nodes as unknown as Node[], edges: ctx.edges as unknown as Edge[] },
    { source: source.nodeId, sourceHandle: source.handle, target: targetId, targetHandle: handle },
  ).ok
}

function resolveTo(ctx: OpContext, to: string, source?: ResolvedEndpoint): ResolvedEndpoint | null {
  const dot = to.indexOf('.')
  const part = dot === -1 ? to : to.slice(0, dot)
  const nodeId = ctx.resolveId(part)
  if (!nodeId) {
    ctx.fail({ code: 'UNKNOWN_STEP', message: `Unknown target "${part}"` })
    return null
  }
  // A reroute or subnet pill: one input, `in`.
  if (isPassthroughType(ctx.nodes[ctx.nodeIndex(nodeId)]?.type)) {
    const handle = dot === -1 ? 'in' : to.slice(dot + 1)
    if (handle !== 'in') {
      ctx.fail({
        code: 'UNKNOWN_HANDLE',
        stepId: part,
        handle,
        message: `"${part}" passes its input through; its input is "in"`,
      })
      return null
    }
    return { nodeId, handle: 'in' }
  }
  const schema = ctx.schemaOf(nodeId)
  if (!schema) {
    ctx.fail(unknownType(ctx, part, nodeId))
    return null
  }

  if (dot !== -1) {
    const handle = to.slice(dot + 1)
    const inputDef = schema.inputs.find((i) => i.id === handle)
    if (!inputDef && schema.inputHandler?.type !== 'multi') {
      ctx.fail({
        code: 'UNKNOWN_HANDLE',
        stepId: part,
        handle,
        message: `"${part}" has no input "${handle}"`,
        suggestion: `Available: ${schema.inputs.map((i) => i.id).join(', ') || '(dynamic input_N)'}`,
      })
      return null
    }
    return { nodeId, handle }
  }

  // Bare target — infer the input handle
  if (schema.inputHandler?.type === 'multi' && schema.inputs.length === 0) {
    // Dynamic handles: next free input_N
    const used = new Set(
      ctx.edges.filter((e) => e.target === nodeId).map((e) => e.targetHandle ?? ''),
    )
    let n = 1
    while (used.has(`input_${n}`)) n++
    return { nodeId, handle: `input_${n}` }
  }

  const connected = new Set(
    ctx.edges.filter((e) => e.target === nodeId).map((e) => e.targetHandle ?? ''),
  )
  const candidates = schema.inputs.filter(
    (i) => !connected.has(i.id) && (!source || fitsInput(ctx, source, nodeId, i.id)),
  )
  if (candidates.length === 1) return { nodeId, handle: candidates[0].id }
  if (candidates.length === 0) {
    ctx.fail({
      code: 'UNKNOWN_HANDLE',
      stepId: part,
      message: `"${part}" has no free compatible input`,
      suggestion: `Inputs: ${schema.inputs.map((i) => i.id).join(', ')}`,
    })
    return null
  }
  ctx.fail({
    code: 'AMBIGUOUS_HANDLE',
    stepId: part,
    message: `"${part}" has ${candidates.length} free compatible inputs — specify one`,
    suggestion: `Candidates: ${candidates.map((i) => i.id).join(', ')}`,
  })
  return null
}

function opConnect(ctx: OpContext, from: string, to: string): boolean {
  const source = resolveFrom(ctx, from)
  if (!source) return false
  const target = resolveTo(ctx, to, source)
  if (!target) return false

  // The editor's own check (utils/graph-commands), with whatever is on that
  // input now set aside: a wire the user could not draw is refused here, with
  // the canvas's reason, not only by the post-batch validation.
  const verdict = canConnect(
    {
      nodes: ctx.nodes as unknown as Node[],
      edges: ctx.edges.filter(
        (e) => !(e.target === target.nodeId && e.targetHandle === target.handle),
      ) as unknown as Edge[],
    },
    {
      source: source.nodeId,
      sourceHandle: source.handle,
      target: target.nodeId,
      targetHandle: target.handle,
    },
  )
  if (!verdict.ok) {
    ctx.fail({
      code: 'INCOMPATIBLE_CONNECTION',
      stepId: to.split('.')[0],
      handle: target.handle,
      message: `"${from}" cannot feed "${to}": ${verdict.reason}`,
    })
    return false
  }

  // Replace any existing connection on this input (last write wins)
  ctx.edges = ctx.edges.filter(
    (e) => !(e.target === target.nodeId && e.targetHandle === target.handle),
  )
  ctx.edges.push({
    id: `e-${source.nodeId}-${source.handle}-${target.nodeId}-${target.handle}`,
    source: source.nodeId,
    sourceHandle: source.handle,
    target: target.nodeId,
    targetHandle: target.handle,
  })

  // A reroute or subnet pill keeps no input entries.
  if (isPassthroughType(ctx.nodes[ctx.nodeIndex(target.nodeId)]?.type)) return true
  const targetSchema = ctx.schemaOf(target.nodeId)
  const inputDef = targetSchema?.inputs.find((i) => i.id === target.handle)
  ctx.mutateNode(target.nodeId, (node) => {
    const inputs = (Array.isArray(node.data.inputs) ? node.data.inputs : []) as MaterializedInput[]
    const entry: MaterializedInput = {
      name: target.handle,
      inputType: 'node',
      source: source.nodeId,
      field:
        source.field ??
        (source.outputType === 'ohlcv' ? 'close' : inputDef?.fieldSelection?.defaultField),
      value: null,
    }
    const idx = inputs.findIndex((i) => i?.name === target.handle)
    if (idx >= 0) inputs[idx] = entry
    else inputs.push(entry)
    node.data.inputs = inputs
    if (inputDef?.discriminatedUnion) {
      node.data.parameters = setParams(node.data.parameters, {
        [inputDef.discriminatedUnion.parameterName]: 'node',
      })
    }
  })
  return true
}

function opDisconnect(ctx: OpContext, op: { to: string }): void {
  const dot = op.to.indexOf('.')
  if (dot === -1) {
    ctx.fail({
      code: 'INVALID_REF',
      message: `disconnect needs "stepId.inputHandle", got "${op.to}"`,
    })
    return
  }
  const target = resolveTo(ctx, op.to)
  if (!target) return

  const before = ctx.edges.length
  ctx.edges = ctx.edges.filter(
    (e) => !(e.target === target.nodeId && e.targetHandle === target.handle),
  )
  if (ctx.edges.length === before) {
    ctx.fail({
      code: 'UNKNOWN_HANDLE',
      stepId: op.to,
      message: `Nothing connected at "${op.to}"`,
    })
    return
  }
  resetInputEntry(ctx, target.nodeId, target.handle)
}

function resetInputEntry(ctx: OpContext, nodeId: string, handle: string): void {
  ctx.mutateNode(nodeId, (node) => {
    const inputs = (Array.isArray(node.data.inputs) ? node.data.inputs : []) as MaterializedInput[]
    const idx = inputs.findIndex((i) => i?.name === handle)
    if (idx >= 0) inputs[idx] = { name: handle, inputType: 'node', source: null, value: null }
    node.data.inputs = inputs
  })
}

// ── remove_node ─────────────────────────────────────────────

function opRemoveNode(ctx: OpContext, id: string): void {
  const nodeId = ctx.resolveId(id)
  if (!nodeId) {
    ctx.fail({ code: 'UNKNOWN_STEP', stepId: id, message: `Unknown step "${id}"` })
    return
  }
  // The spine (Stock Selection, main Price Data, Trade Manager, Report) is
  // never removed; the editor refuses the same delete from the canvas.
  const guarded = guardSpineDelete({
    nodes: ctx.nodes as unknown as Node[],
    edges: ctx.edges as unknown as Edge[],
    nodeIds: [nodeId],
  })
  if (guarded.nodeIds.length === 0) {
    ctx.fail({
      code: 'INVALID_REF',
      stepId: id,
      message: `remove_node: "${id}" is the strategy's ${guarded.keptNames[0]} and cannot be removed`,
      suggestion: 'Change its params with set_params instead.',
    })
    return
  }
  const downstream = ctx.edges.filter((e) => e.source === nodeId)
  ctx.edges = ctx.edges.filter((e) => e.source !== nodeId && e.target !== nodeId)
  ctx.nodes = ctx.nodes.filter((n) => n.id !== nodeId)
  for (const edge of downstream) {
    if (edge.targetHandle) resetInputEntry(ctx, edge.target, edge.targetHandle)
  }
  for (const [stepId, mapped] of ctx.idMap) {
    if (mapped === nodeId) ctx.idMap.delete(stepId)
  }
}

// ── insert_between ──────────────────────────────────────────

function opInsertBetween(
  ctx: OpContext,
  op: { id: string; type: string; from: string; to: string; params?: Record<string, unknown> },
): void {
  const sourceId = ctx.resolveId(op.from.split('.')[0])
  const targetId = ctx.resolveId(op.to.split('.')[0])
  if (!sourceId || !targetId) {
    ctx.fail({
      code: 'UNKNOWN_STEP',
      message: `insert_between: unknown step "${!sourceId ? op.from : op.to}"`,
    })
    return
  }
  const edge = ctx.edges.find((e) => e.source === sourceId && e.target === targetId)
  if (!edge) {
    ctx.fail({
      code: 'INVALID_REF',
      message: `No edge between "${op.from}" and "${op.to}" to split`,
    })
    return
  }

  const sourceNode = ctx.nodes[ctx.nodeIndex(sourceId)]
  const targetNode = ctx.nodes[ctx.nodeIndex(targetId)]
  const midpoint = {
    x: (sourceNode.position.x + targetNode.position.x) / 2,
    y: (sourceNode.position.y + targetNode.position.y) / 2,
  }

  // Resolve the new node's handles BEFORE mutating anything: insertion has an
  // obvious convention (first compatible handle), so unlike bare `connect`,
  // ambiguity here resolves deterministically instead of erroring.
  const newSchema = UniversalNodeRegistry.getSchemaResolved(op.type)
  if (!newSchema) {
    ctx.fail({ code: 'UNKNOWN_TYPE', stepId: op.id, message: `Unknown node type "${op.type}"` })
    return
  }
  const sourceSchema = ctx.schemaOf(sourceId)
  const sourceOutput = sourceSchema?.outputs.find((o) => o.id === edge.sourceHandle)
  const targetSchema = ctx.schemaOf(targetId)
  const targetInputDef = targetSchema?.inputs.find((i) => i.id === edge.targetHandle)

  // The canvas's rule for both halves of the split (passthrough sources, a
  // reroute or a subnet pill, are type-transparent and pass).
  const newData = { parameters: op.params ?? {} }
  const newIn =
    newSchema.inputs.find(
      (i) =>
        !sourceOutput ||
        isPassthroughType(sourceNode.type) ||
        validateNodeConnection(
          sourceSchema?.type ?? '',
          newSchema.type,
          i.id,
          sourceOutput.id,
          undefined,
          newData,
        ).isValid,
    ) ?? (newSchema.inputHandler?.type === 'multi' ? { id: 'input_1' } : undefined)
  const newOut =
    newSchema.outputs.find(
      (o) =>
        o.passthrough ||
        !targetInputDef ||
        validateNodeConnection(
          newSchema.type,
          targetSchema?.type ?? '',
          targetInputDef.id,
          o.id,
          undefined,
          targetNode.data as Record<string, unknown>,
        ).isValid,
    ) ?? newSchema.outputs[0]
  if (!newIn || !newOut) {
    ctx.fail({
      code: 'INCOMPATIBLE_CONNECTION',
      stepId: op.id,
      message: `"${op.type}" cannot sit between "${op.from}" and "${op.to}"`,
      suggestion: `It has no ${!newIn ? 'input' : 'output'} compatible with the split edge`,
    })
    return
  }

  const newNode = opAddNode(ctx, op, midpoint)
  if (!newNode) return

  // Remove the split edge, then wire source → new → target explicitly
  ctx.edges = ctx.edges.filter((e) => e.id !== edge.id)
  if (edge.targetHandle) resetInputEntry(ctx, targetId, edge.targetHandle)

  const fromRef = edge.sourceHandle ? `${sourceId}.${edge.sourceHandle}` : sourceId
  if (!opConnect(ctx, fromRef, `${newNode.id}.${newIn.id}`)) return
  const toRef = edge.targetHandle ? `${targetId}.${edge.targetHandle}` : targetId
  opConnect(ctx, `${newNode.id}.${newOut.id}`, toRef)
}

// ── replace_node ────────────────────────────────────────────

function opReplaceNode(
  ctx: OpContext,
  op: { id: string; type: string; params?: Record<string, unknown> },
): void {
  const nodeId = ctx.resolveId(op.id)
  if (!nodeId) {
    ctx.fail({ code: 'UNKNOWN_STEP', stepId: op.id, message: `Unknown step "${op.id}"` })
    return
  }
  const newSchema = UniversalNodeRegistry.getSchemaResolved(op.type)
  if (!newSchema) {
    ctx.fail({ code: 'UNKNOWN_TYPE', stepId: op.id, message: `Unknown node type "${op.type}"` })
    return
  }

  const oldNode = ctx.nodes[ctx.nodeIndex(nodeId)]
  // Swapping a spine node to another type would remove it just the same.
  if (oldNode.type !== newSchema.type) {
    const guarded = guardSpineDelete({
      nodes: ctx.nodes as unknown as Node[],
      edges: ctx.edges as unknown as Edge[],
      nodeIds: [nodeId],
    })
    if (guarded.nodeIds.length === 0) {
      ctx.fail({
        code: 'INVALID_REF',
        stepId: op.id,
        message: `replace_node: "${op.id}" is the strategy's ${guarded.keptNames[0]} and cannot change type`,
        suggestion: 'Change its params with set_params instead.',
      })
      return
    }
  }
  const oldPosition = { ...oldNode.position }
  const incoming = ctx.edges.filter((e) => e.target === nodeId)
  const outgoing = ctx.edges.filter((e) => e.source === nodeId)

  // Materialize the replacement in place (same node id → downstream edges stay valid)
  const result = materializeNode(
    { id: nodeId, type: newSchema.type, params: op.params },
    { schemaById: new Map([[nodeId, newSchema]]), position: oldPosition },
  )
  if (result.errors.length > 0) {
    ctx.errors.push(...result.errors.map((e) => ({ ...e, stepId: op.id })))
    return
  }
  const idx = ctx.nodeIndex(nodeId)
  ctx.nodes = [...ctx.nodes]
  ctx.nodes[idx] = result.node

  // Rewire incoming: keep when the new schema has the same input id
  for (const edge of incoming) {
    const keep = newSchema.inputs.some((i) => i.id === edge.targetHandle)
    if (keep && edge.targetHandle) {
      const fromRef = edge.sourceHandle ? `${edge.source}.${edge.sourceHandle}` : edge.source
      ctx.edges = ctx.edges.filter((e) => e.id !== edge.id)
      opConnect(ctx, fromRef, `${nodeId}.${edge.targetHandle}`)
    } else {
      ctx.edges = ctx.edges.filter((e) => e.id !== edge.id)
      ctx.warnings.push({
        code: 'DROPPED_CONNECTION',
        stepId: op.id,
        handle: edge.targetHandle ?? undefined,
        message: `"${op.type}" has no input "${edge.targetHandle}" — connection dropped`,
      })
    }
  }

  // Rewire outgoing: same output id, or remap when the new node has one output
  for (const edge of outgoing) {
    const sameId = newSchema.outputs.find((o) => o.id === edge.sourceHandle)
    const remapped = sameId?.id ?? (newSchema.outputs.length === 1 ? newSchema.outputs[0].id : null)
    ctx.edges = ctx.edges.filter((e) => e.id !== edge.id)
    if (remapped && edge.targetHandle) {
      opConnect(ctx, `${nodeId}.${remapped}`, `${edge.target}.${edge.targetHandle}`)
    } else {
      if (edge.targetHandle) resetInputEntry(ctx, edge.target, edge.targetHandle)
      ctx.warnings.push({
        code: 'DROPPED_CONNECTION',
        stepId: op.id,
        handle: edge.sourceHandle ?? undefined,
        message: `"${op.type}" has no output matching "${edge.sourceHandle}" — connection dropped`,
      })
    }
  }
}
